import { ref } from 'vue'
import { ToastInterface, ToastType } from './type'
import { AUTH_MESSAGES } from './constant/messages'

// TOAST
export class Toast {
  private list = ref<ToastInterface[]>([])
  private timeoutMap = new Map<number, ReturnType<typeof setTimeout>>()
  //이 배열에 포함되는 toast id가 리스트에 있다면, 그 이후로 추가되는 toast는 무시됩니다.
  private blockingToastKeys = ref<string[]>([AUTH_MESSAGES.SESSION_EXPIRED_LOGOUT])
  public isResetting = ref(false)
  get(): ToastInterface[] {
    return this.list.value
  }
  // 모든 토스트를 즉시 제거하고 타이머를 청소하는 기능 추가
  clearAll(): void {
    this.timeoutMap.forEach(timeout => clearTimeout(timeout))
    this.timeoutMap.clear()

    // 1. 리셋 플래그 활성화
    this.isResetting.value = true
    // 2. 리스트 비우기
    this.list.value = []

    // 3. 다음 틱이나 짧은 시간 후 플래그 해제 (새 토스트는 다시 애니메이션 되어야 함)
    setTimeout(() => {
      this.isResetting.value = false
    }, 50)
  }

  addedBlockingToast(message?: string): boolean {
    if (message && this.blockingToastKeys.value.includes(message)) {
      this.clearAll() // 단순히 리스트만 비우는 게 아니라 전체 청소
      return true // 블로킹 발생 여부 반환
    }
    return false
  }

  /**
   * 토스트 추가 (같은 타입/문구가 있으면 duration 연장)
   * @param type - 토스트 타입
   * @param message - 메시지
   * @param duration - 표시 시간 (ms)
   * @returns 토스트 ID
   */
  add(type: ToastType, message?: string, duration: number = 3000): number {
    // 블로킹 토스트 메시지가 들어오면 모든 토스트를 제거
    this.addedBlockingToast(message)
    // 기본 메시지 설정
    let finalMessage = message
    if (type === 'error' && !message) finalMessage = '오류가 발생했습니다. 다시 시도해주세요.'
    else if (!message) finalMessage = ''

    // 같은 타입과 문구의 토스트가 이미 있는지 확인
    const existingToast = this.list.value.find(
      toast => toast.type === type && toast.message === finalMessage,
    )

    if (existingToast && existingToast.id !== undefined) {
      const existingId = existingToast.id

      // 기존 타이머 취소
      const existingTimeout = this.timeoutMap.get(existingId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        this.timeoutMap.delete(existingId)
      }

      // 새 타이머 설정
      if (duration > 0) {
        const timeout = setTimeout(() => {
          this.remove(existingId)
        }, duration)
        this.timeoutMap.set(existingId, timeout)
      }

      return existingId
    }

    // 새 토스트 추가
    const id = Date.now()
    const newToast = { id, message: finalMessage, type, duration } as ToastInterface

    this.list.value.push(newToast)

    // 시간이 0인 경우 계속 띄워둠
    if (duration > 0) {
      const timeout = setTimeout(() => {
        this.remove(id)
      }, duration)
      this.timeoutMap.set(id, timeout)
    }

    return id
  }

  remove(id: number): void {
    // 타이머 정리
    const timeout = this.timeoutMap.get(id)
    if (timeout) {
      clearTimeout(timeout)
      this.timeoutMap.delete(id)
    }

    this.list.value = this.list.value.filter(toast => toast.id !== id)
  }

  updateMessage(id: number, newMessage: string | ((oldMessage: string) => string)): void {
    const toast = this.list.value.find(t => t.id === id)
    if (toast) {
      if (typeof newMessage === 'function') {
        toast.message = newMessage(toast.message || '')
      } else {
        toast.message = newMessage
      }
    }
  }
}
